import { LitElement, html, css, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import type { UWidgetSpec, UWidgetEvent } from '../core/types.js';
import { toEChartsOption, chartLayoutKey, type ChartSize } from '../renderers/echarts-adapter.js';
import { themeStyles } from '../styles/tokens.js';
import { onPageThemeChange } from '../theme-watch.js';

// Tree-shakeable ECharts imports
import * as echarts from 'echarts/core';
import { BarChart, LineChart, PieChart, ScatterChart, RadarChart, HeatmapChart, BoxplotChart, FunnelChart, TreemapChart, CustomChart } from 'echarts/charts';
import {
  GridComponent,
  TooltipComponent,
  LegendComponent,
  RadarComponent,
  MarkLineComponent,
  VisualMapComponent,
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';

echarts.use([
  BarChart,
  LineChart,
  PieChart,
  ScatterChart,
  RadarChart,
  HeatmapChart,
  BoxplotChart,
  FunnelChart,
  TreemapChart,
  CustomChart,
  GridComponent,
  TooltipComponent,
  LegendComponent,
  RadarComponent,
  MarkLineComponent,
  VisualMapComponent,
  CanvasRenderer,
]);

@customElement('uw-chart')
export class UwChart extends LitElement {
  static styles = [
    themeStyles,
    css`
      





      :host {
        display: flex;
        flex-direction: column;
        container: uw-chart / inline-size;
      }

      .chart-container {
        width: 100%;
        flex: 1 1 auto;
        min-height: 0;
        height: var(--u-widget-chart-height, 300px);
      }

      @container uw-chart (max-width: 20rem) {
        .chart-container {
          height: var(--u-widget-chart-height-narrow, 200px);
        }
      }
    `,
  ];

  @property({ type: Object })
  spec: UWidgetSpec | null = null;

  private _chart: echarts.ECharts | null = null;
  private _container: HTMLDivElement | null = null;
  private _resizeObserver: ResizeObserver | null = null;
  /** The layout the current option was built for (see chartLayoutKey) — a resize that changes it rebuilds. */
  private _layoutKey = '';
  /** The theme tokens the current option was built with (see _themeKey) — a page theme change that moves them redraws. */
  private _themeKey = '';
  private _unwatchTheme: (() => void) | null = null;

  connectedCallback() {
    super.connectedCallback();
    // The canvas holds the colours it read when it drew; a page that switches its theme without touching this
    // widget (a `theme` attribute on <html>, a `dark` class, the system scheme) leaves it painted in the old one.
    this._unwatchTheme = onPageThemeChange(() => {
      if (this._chart && this._themeKey !== this._readThemeKey()) this._updateChart();
    });
  }

  render() {
    if (!this.spec) return nothing;
    const label = this.spec.title ?? `${this.spec.widget} chart`;
    return html`<div class="chart-container" part="chart" role="img" aria-label=${label}></div>`;
  }

  protected firstUpdated() {
    this._container = this.shadowRoot?.querySelector('.chart-container') as HTMLDivElement | null;
    this._initChart();
    if (this._container) {
      this._resizeObserver = new ResizeObserver(() => {
        this._chart?.resize();
        if (chartLayoutKey(this._canvasSize()) !== this._layoutKey) this._updateChart();
      });
      this._resizeObserver.observe(this._container);
    }
  }

  protected updated(changed: Map<string, unknown>) {
    if (changed.has('spec') && this._container) {
      this._updateChart();
    }
  }

  private _initChart() {
    if (!this._container || !this.spec) return;

    try {
      this._chart = echarts.init(this._container);
      this._chart.on('click', (params: Record<string, unknown>) => {
        this._onChartClick(params);
      });
      this._updateChart();
    } catch (e) {
      console.warn('[@iyulab/u-widgets] uw-chart: failed to initialize ECharts:', (e as Error).message);
    }
  }

  private _onChartClick(params: Record<string, unknown>) {
    if (!this.spec) return;
    const detail: UWidgetEvent = {
      type: 'select',
      widget: this.spec.widget,
      id: this.spec.id,
      data: {
        name: params.name as string,
        seriesName: params.seriesName as string,
        value: params.value,
        dataIndex: params.dataIndex as number,
      },
    };
    // Charts that split rows across series (color groups) or skip rows tag each mark with its
    // spec.data row, since dataIndex is then local to the series. Reported as _index — the same key
    // table and list use for the selected row's index in spec.data.
    const item = params.data;
    if (item && typeof item === 'object' && !Array.isArray(item)
      && typeof (item as Record<string, unknown>).rowIndex === 'number') {
      detail.data!._index = (item as Record<string, unknown>).rowIndex;
    }
    this.dispatchEvent(
      new CustomEvent('u-widget-internal', {
        detail,
        bubbles: true,
        composed: true,
      }),
    );
  }

  private _updateChart() {
    if (!this._chart || !this.spec) return;

    const size = this._canvasSize();
    this._layoutKey = chartLayoutKey(size);
    this._themeKey = this._readThemeKey();
    const option = toEChartsOption(this.spec, size);

    // Inject theme colors from CSS custom properties,
    // but only if the spec didn't provide explicit colors
    const hasSpecColors = Array.isArray(
      (this.spec.options as Record<string, unknown> | undefined)?.colors,
    ) && ((this.spec.options as Record<string, unknown>).colors as unknown[]).length > 0;
    if (!hasSpecColors) {
      const themeColors = this._readThemeColors();
      if (themeColors.length > 0) {
        option.color = themeColors;
      }
    }
    const hasExplicitBg = !!(
      (this.spec.options as Record<string, unknown> | undefined)?.echarts as Record<string, unknown> | undefined
    )?.backgroundColor;
    if (!hasExplicitBg && this._readCSSVar('--u-widget-bg')) {
      option.backgroundColor = 'transparent';
    }

    // Inject text/axis/grid theme from CSS variables
    this._applyThemeStyle(option);

    this._chart.setOption(option, true);
  }

  private _canvasSize(): ChartSize | undefined {
    if (!this._container) return undefined;
    return { width: this._container.clientWidth, height: this._container.clientHeight };
  }

  private _applyThemeStyle(option: Record<string, unknown>) {
    const textColor = this._readCSSVar('--u-widget-text');
    const secondaryColor = this._readCSSVar('--u-widget-text-secondary');
    const borderColor = this._readCSSVar('--u-widget-border');

    if (!textColor) return; // no theme vars available

    // Global text style
    option.textStyle = { ...(option.textStyle as Record<string, unknown>), color: textColor };

    // Axis styling — deep-merge into existing axis config preserving all existing properties
    const mergeAxisTheme = (axis: unknown): unknown => {
      if (!axis || typeof axis !== 'object') return axis;
      if (Array.isArray(axis)) return axis.map(mergeAxisTheme);
      const a = axis as Record<string, unknown>;
      const existingLine = a.axisLine as Record<string, unknown> ?? {};
      const existingLineStyle = existingLine.lineStyle as Record<string, unknown> ?? {};
      const existingSplit = a.splitLine as Record<string, unknown> ?? {};
      const existingSplitStyle = existingSplit.lineStyle as Record<string, unknown> ?? {};
      return {
        ...a,
        axisLabel: { ...(a.axisLabel as Record<string, unknown> ?? {}), color: secondaryColor || textColor },
        axisLine: { ...existingLine, lineStyle: { ...existingLineStyle, color: borderColor || secondaryColor } },
        splitLine: { ...existingSplit, lineStyle: { ...existingSplitStyle, color: borderColor } },
      };
    };
    if (option.xAxis) option.xAxis = mergeAxisTheme(option.xAxis);
    if (option.yAxis) option.yAxis = mergeAxisTheme(option.yAxis);

    // Legend — merge textStyle preserving existing properties
    if (option.legend && typeof option.legend === 'object') {
      const leg = option.legend as Record<string, unknown>;
      option.legend = { ...leg, textStyle: { ...(leg.textStyle as Record<string, unknown> ?? {}), color: textColor } };
    }
  }

  private _readThemeColors(): string[] {
    const colors: string[] = [];
    // Read --u-widget-chart-color-N (1-based) custom properties
    for (let i = 1; i <= 10; i++) {
      const c = this._readCSSVar(`--u-widget-chart-color-${i}`);
      if (c) colors.push(c);
      else break;
    }
    // Fallback: use --u-widget-primary as first color if no palette defined
    if (colors.length === 0) {
      const primary = this._readCSSVar('--u-widget-primary');
      if (primary) colors.push(primary);
    }
    return colors;
  }

  private _readCSSVar(name: string): string {
    return getComputedStyle(this).getPropertyValue(name).trim();
  }

  /** Every token _updateChart reads, in one string — equal keys draw the same chart. */
  private _readThemeKey(): string {
    return [
      ...this._readThemeColors(),
      this._readCSSVar('--u-widget-bg'),
      this._readCSSVar('--u-widget-text'),
      this._readCSSVar('--u-widget-text-secondary'),
      this._readCSSVar('--u-widget-border'),
    ].join('|');
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._unwatchTheme?.();
    this._unwatchTheme = null;
    this._resizeObserver?.disconnect();
    this._resizeObserver = null;
    this._chart?.dispose();
    this._chart = null;
  }

  /** Resize the chart (call when container size changes). */
  resize() {
    this._chart?.resize();
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'uw-chart': UwChart;
  }
}
